import { DASHBOARD_URL } from '@/env'
import { authService } from '@/features/authorization/api/authService'
import { useAuthComplete } from '@/features/authorization/hooks/useAuthComplete'
import { routes } from '@/pages/routes'
import { Button, Text } from '@/ui-kit'
import { Trans } from '@lingui/react/macro'
import { AxiosError } from 'axios'
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

type GoogleAuthState = 'loading' | 'error'

export const GoogleAuthPage = () => {
  const [searchParams] = useSearchParams()
  const { complete } = useAuthComplete()
  const [state, setState] = useState<GoogleAuthState>('loading')
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    let cancelled = false

    const authenticate = async () => {
      const code = searchParams.get('code')
      const inviteCode = searchParams.get('state') || undefined

      if (!code) {
        setState('error')
        setErrorMessage('Missing authorization code')
        return
      }

      const redirectUri = `${DASHBOARD_URL}/auth/google`

      try {
        const { token } = await authService.loginWithGoogleCode(code, redirectUri)
        if (!cancelled) {
          await complete(token, inviteCode)
        }
      } catch (err) {
        if (!cancelled) {
          const axiosError = err as AxiosError<{ message?: string }>
          setState('error')
          setErrorMessage(axiosError.response?.data?.message || 'Google sign-in failed. Please try again.')
        }
      }
    }

    authenticate()

    return () => {
      cancelled = true
    }
  }, [])

  const handleBackToLogin = useCallback(() => {
    window.location.replace(routes.login.path)
  }, [])

  return (
    <>
      {state === 'loading' && (
        <>
          <Text className="mb-4 text-center" type="h3">
            <Trans>Signing in...</Trans>
          </Text>
          <Text className="colorWhiteToBlack my-2 text-center">
            <Trans>Please wait while we complete your Google sign-in.</Trans>
          </Text>
        </>
      )}

      {state === 'error' && (
        <>
          <Text className="mb-4 text-center" type="h3">
            <Trans>Sign-in Failed</Trans>
          </Text>
          <Text className="colorWhiteToBlack my-2 text-center">{errorMessage}</Text>
          <Button className="mt-4" onClick={handleBackToLogin}>
            <span className="colorWhite">
              <Trans>Back to login</Trans>
            </span>
          </Button>
        </>
      )}
    </>
  )
}

export default GoogleAuthPage
